import React from "react";
import DocsLayout, { Section, Steps, Callout, JumpNav, AvailableTo } from "./DocsLayout";

const Img = ({ src, alt }: { src: string; alt: string }) => (
  <img src={src} alt={alt} style={{ width: "100%", borderRadius: 8, border: "1px solid #e2e8f0", margin: "16px 0", display: "block" }} />
);

const COLOR = "#ec4899";
const ALL_ROLES = ["admin", "admin_assistant", "hr", "recruiter", "finance", "employee"] as const;

const CONTEXT_ROWS: [string, string][] = [
  ["Employee", "Own leave balances & requests, latest payslip, attendance this month, pending expenses, upcoming holidays, manager info, timesheet hours by project, active performance review status, assigned assets."],
  ["Recruiter", "Everything an Employee sees, plus: job pipeline counts per active job, the 10 most recent applications with scores, upcoming interviews, offer letter status, new candidates this week."],
  ["HR", "Everything a Recruiter sees, plus: headcount by department, recent joiners, who's on leave today/this week, pending leave and expense approvals, pending exit requests, review-cycle completion %, recently issued HR letters."],
  ["Finance", "Own data, plus: invoice summary (paid/sent/draft, revenue, overdue), aging buckets, recent invoices, open quotations, pending expense claims, this month's payroll cost."],
  ["Admin / Admin Assistant", "Everything above, combined — HR + Finance + Recruiter context in one place, since Admin has full access."],
];

const ACTION_ROWS: [string, string, string][] = [
  ["Apply for Leave", "Employee, HR, Recruiter, Finance, Admin, Admin Assistant, Manager", "\"Apply for annual leave next Monday to Wednesday\""],
  ["Approve / Reject Leave", "HR, Admin, Admin Assistant, Manager", "\"Approve Bob's leave request\""],
  ["Approve / Reject Expense", "HR, Finance, Admin, Admin Assistant, Manager", "\"Approve the expense claim from Alice\""],
  ["Schedule / Cancel Interview", "HR, Recruiter, Admin, Admin Assistant", "\"Schedule an interview with Carol for tomorrow at 3pm\""],
  ["Advance Candidate", "HR, Recruiter, Admin, Admin Assistant", "\"Move David to Shortlisted\""],
  ["Post Announcement", "HR, Admin, Admin Assistant", "\"Post an announcement about the office closure on Friday\""],
  ["Resolve Helpdesk Ticket", "HR, Admin, Admin Assistant, Manager", "\"Mark ticket #42 as resolved\""],
];

export default function AIAssistant() {
  return (
    <DocsLayout>
      <div style={{ marginBottom: 32 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: COLOR + "15", border: `1px solid ${COLOR}30`,
          borderRadius: 8, padding: "5px 14px", marginBottom: 16,
        }}>
          <span style={{ fontSize: 16 }}>🤖</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: COLOR }}>AI Chat Assistant</span>
        </div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>AI Chat Assistant</h1>
        <p style={{ fontSize: 15, color: "#64748b", maxWidth: 660, lineHeight: 1.6 }}>
          Meet Aria — the assistant built into every page. It's not a generic chatbot: it sees your real
          HR data, understands natural language and relative dates, and can take real actions on your
          behalf with your confirmation.
        </p>
      </div>

      <JumpNav color={COLOR} items={[
        { id: "how-it-works", label: "How It Works" },
        { id: "context", label: "What It Knows, By Role" },
        { id: "actions", label: "What It Can Do" },
        { id: "limits", label: "Usage Limits" },
      ]} />

      <Section id="how-it-works" title="How It Works">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Click the chat bubble (bottom-right corner of any page) to talk to Aria in plain language.
        </p>
        <Img src="/docs/screenshots/employee-chat.png" alt="AI Chat widget" />
        <Steps items={[
          "Ask about your data — e.g. \"How many annual leave days do I have left?\" — answers are drawn from your actual HR records, not generic text.",
          "Use relative dates — \"apply for leave next Monday to Wednesday\" or \"what was my attendance last month\" are understood without typing exact dates.",
          "Keep the conversation going — follow-ups like \"and the day after that too\" or \"make it sick leave instead\" refer back to what you just said.",
          "If a request is ambiguous — e.g. two employees named Sam — the assistant asks a clarifying question instead of guessing.",
          "Ask it to do something (see What It Can Do below) — it shows a confirmation card with the details before anything happens.",
        ]} />
        <Callout
          icon="🔒"
          title="Confirmation before action"
          body="Aria never takes a high-risk or email-triggering action silently. Every action request shows a confirmation card with the exact details first — you approve it before anything is saved or sent."
        />
      </Section>

      <Section id="context" title="What It Knows, By Role">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Each role gets a different, purpose-built context pulled from your real data before Aria answers
          — so responses are relevant instead of generic. Admin and Admin Assistant see everything below
          combined.
        </p>
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" as any }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 640 }}>
            <thead>
              <tr style={{ background: "#f1f5f9" }}>
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "#0f172a", borderBottom: "2px solid #e2e8f0", width: 160 }}>Role</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "#0f172a", borderBottom: "2px solid #e2e8f0" }}>What Aria sees</th>
              </tr>
            </thead>
            <tbody>
              {CONTEXT_ROWS.map((row, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                  <td style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0", fontWeight: 600, color: "#0f172a", verticalAlign: "top" }}>{row[0]}</td>
                  <td style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0", color: "#475569", lineHeight: 1.6 }}>{row[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="actions" title="What It Can Do">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Beyond answering questions, Aria can perform real actions — each gated by the same role
          permissions as the equivalent page in the app, and always shown as a confirmation card first.
        </p>
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" as any }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 640 }}>
            <thead>
              <tr style={{ background: "#f1f5f9" }}>
                {["Action", "Available to", "Example"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "#0f172a", borderBottom: "2px solid #e2e8f0" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ACTION_ROWS.map((row, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #e2e8f0", fontWeight: 600, color: "#0f172a" }}>{row[0]}</td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #e2e8f0", color: "#475569" }}>{row[1]}</td>
                  <td style={{ padding: "9px 12px", borderBottom: "1px solid #e2e8f0", color: "#64748b", fontStyle: "italic" }}>{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="limits" title="Usage Limits">
        <AvailableTo roles={[...ALL_ROLES]} />
        <Callout
          icon="ℹ️"
          title="10 messages a day for Employees and Contractors — everyone else is unlimited"
          body="HR, Recruiter, Finance, Admin, and Admin Assistant have no daily limit. Employees and Contractors get 10 AI chat messages per day; contact your admin if you need more."
        />
      </Section>
    </DocsLayout>
  );
}
